import { alertController, modalController } from '@ionic/core';
import { Component, Event, EventEmitter, forceUpdate, Fragment, h, Prop, State } from '@stencil/core';
import { DateUtils, PersonDoc, Workflow, WorkflowState } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { ConcourseDb } from '../../global/ConcourseDb';
import { User } from '../../global/models';
import { WorkflowStateModal } from './workflow-state-modal';
import { TimelineBubble } from '../../global/models/Timeline';
import { SubmittalUtils } from '..';
import { ScheduleService, TimelineUtils } from '../../global';
import { ProjectService } from '../../projects/services/ProjectService';
import { Calendar } from '../../components/concourse-schedule/model/Calendar';

// import { SubmittalSettingsDoc } from 'common';

// Removed local SubmittalDoc interface, now using the shared one

@Component({
  tag: 'workflow-editor-modal',
  styleUrl: 'concourse-submittals.css',
  //shadow: true,
})
export class WorkflowEditorModal {
  static async present(projectId: string, workflow: Workflow, feature: string): Promise<Workflow | string | null> {
    const modal = await modalController.create({
      component: 'workflow-editor-modal',
      componentProps: { projectId, workflow, feature },
      cssClass: 'modal-height-auto concourse-modal',
      backdropDismiss: false,
    });
    return new Promise(async resolve => {
      modal.addEventListener('save', (e: CustomEvent) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('delete', (e: CustomEvent) => {
        modal.dismiss();
        resolve('delete');
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  @Prop() projectId;
  @Prop() workflow: Workflow;
  @Prop() feature: string;
  @Prop() submitStateId: string;

  @State() _workflow: Workflow;
  @State() editIndex: number = -1;
  @State() errorMessage: string = '';
  @Event() save: EventEmitter<Workflow>;
  @Event() delete: EventEmitter<void>;
  @Event() dismiss: EventEmitter<void>;

  @State() calendar: Calendar;

  private people: PersonDoc[];
  get user(): User {
    return globalThis.user;
  }

  get dueDate(): Date {
    const today = DateUtils.currentDate.getTime();
    const pendingDays = this._workflow.states.reduce((sum, st) => sum + st.dueDays, 0) + 7; // 7 days buffer
    return new Date(today + pendingDays * DateUtils.MILLIS_PER_DAY);
  }

  get timelineBubbles(): TimelineBubble[] {
    if (!this.calendar) return [];
    return TimelineUtils.getTimelineBubbles([], this._workflow.states, this.people, this.calendar);
  }

  async componentWillLoad() {
    this._workflow = { ...this.workflow };
    this.people = await PeopleService.getPersonDocs(this.projectId);
    this.loadCalendar();
  }

  private async loadCalendar() {
    const projectDoc = await ProjectService.getProjectDoc(this.projectId);
    this.calendar = await ScheduleService.getCalendarOrDefault(this.projectId, projectDoc.default_calendar_unique_id);
  }

  async editState(stateIndex: number) {
    if (stateIndex == 0) {
      alertController
        .create({
          header: 'Cannot edit create state',
          message: 'The create state cannot be edited',
          buttons: ['OK'],
        })
        .then(alert => alert.present());
      return;
    }

    const state = this._workflow.states[stateIndex];
    // can't delete the default states
    const canDelete = !SubmittalUtils.DEFAULT_SUBMITTAL_WORKFLOW.states.map(s => s.id).includes(state.id);
    const updated = await WorkflowStateModal.present(this.projectId, state, this.feature, null, canDelete);
    if (updated && (updated as WorkflowState)) {
      this._workflow.states[stateIndex] = updated as WorkflowState;
      forceUpdate(this);
    } else if (updated == 'deleted') {
      this._workflow.states.splice(stateIndex, 1);
    } else if (updated && (updated as WorkflowState)?.action) {
      this._workflow.states[stateIndex] = updated as WorkflowState;
    }
    forceUpdate(this);
  }

  async addState(index: number) {
    console.log('Showing pre-pending item:', index);
    const state: WorkflowState = {
      id: ConcourseDb.makeShortDocId(),
      dueBy: [],
      dueDays: 7,
      action: '',
      pastTense: '',
    };
    const updated = await WorkflowStateModal.present(this.projectId, state, this.feature, null, false);
    if (updated && (updated as WorkflowState)) {
      this._workflow.states.splice(index + 1, 0, { ...(updated as WorkflowState), id: ConcourseDb.makeShortDocId() });
      forceUpdate(this);
    }
  }

  async deleteWorkflow() {
    const alert = await alertController.create({
      header: 'Delete Workflow',
      message: 'Are you sure you want to delete this workflow?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Delete', role: 'delete', handler: () => this.delete.emit() },
      ],
    });
    await alert.present();
  }

  async saveSettings() {
    if (!this._workflow.name) {
      this.errorMessage = 'Name is required';
      return;
    }
    this.errorMessage = '';
    this.editIndex = -1;
    this.save.emit(this._workflow);
  }

  render() {
    console.log(`render ${this.timelineBubbles}`);
    return (
      <Fragment>
        <modal-header modalTitle={this.workflow.name ? `Edit ${this.workflow.name}` : `Create New Workflow`} onDismiss={() => this.dismiss.emit()}></modal-header>
        <ion-page class="ion-padding">
          <ion-input
            label="Name"
            class="cc-margin-bottom"
            label-placement="stacked"
            required={true}
            placeholder="Enter a name for the workflow"
            value={this._workflow.name}
            onIonInput={e => (this._workflow.name = e.target.value)}
          ></ion-input>
          <concourse-timeline
            bubbles={this.timelineBubbles}
            dueDate={this.dueDate}
            onBubbleClick={e => this.editState(e.detail[0])}
            onBarClick={e => this.addState(e.detail)}
          ></concourse-timeline>
        </ion-page>
        <ion-footer>
          <ion-text color="danger">{this.errorMessage}</ion-text>
          <ion-toolbar>
            {this.workflow.id && (
              <ion-button slot="start" fill="clear" onClick={() => this.deleteWorkflow()}>
                <ion-icon slot="start" name="trash-outline"></ion-icon>
                Delete
              </ion-button>
            )}
            <ion-button slot="end" fill="solid" color="primary" shape="round" onClick={() => this.saveSettings()}>
              {this.workflow.name ? 'Save Workflow' : 'Create Workflow'}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }
}
